import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { useLocalizeMessage, ModalWindow, Text, Select, Badge, RadioGroup, RadioGroupOption, Input, TextArea, Spinner, Toggle, useToastNotification, } from 'libs.nucleus';
import { performAtomicVersionUpgrade } from '../../../utils/atomic_version_upgrade.utils';
import { DEFAULT_USE_NEW_INFRASTRUCTURE } from '../../constants/data_view.constants';
import { useDataViews } from '../../hooks';
// Helper function to get available status options based on current status
const getAvailableStatusOptions = (currentStatus, translate) => {
    if (currentStatus === 'DRAFT') {
        return [{ label: translate('In review'), value: 'IN_REVIEW' }];
    }
    if (currentStatus === 'IN_REVIEW') {
        return [
            { label: translate('Ready for use'), value: 'PUBLISHED' },
            { label: translate('In progress'), value: 'DRAFT' },
        ];
    }
    if (currentStatus === 'PUBLISHED') {
        return [{ label: translate('In progress'), value: 'DRAFT' }];
    }
    return [];
};
const getStatusBadgeVariant = (status) => {
    switch (status) {
        case 'DRAFT':
            return 'warning';
        case 'IN_REVIEW':
            return 'incomplete';
        case 'PUBLISHED':
            return 'success';
        default:
            return 'common';
    }
};
const getStatusLabel = (status, translate) => {
    switch (status) {
        case 'DRAFT':
            return translate('In progress');
        case 'IN_REVIEW':
            return translate('In review');
        case 'PUBLISHED':
            return translate('Ready');
        default:
            return status;
    }
};
export const ChangeStatusModal = ({ dataView, onClose, onStatusChange }) => {
    const translate = useLocalizeMessage();
    const { addNotification } = useToastNotification();
    const { updateDataView, cloneDataView, archiveDataView, checkViewNameUnique, isLoading: isApiLoading, } = useDataViews();
    const availableOptions = getAvailableStatusOptions(dataView.status, translate);
    const [newStatus, setNewStatus] = useState(availableOptions.length > 0 ? availableOptions[0] : undefined);
    // Action selection for PUBLISHED -> DRAFT
    const [selectedAction, setSelectedAction] = useState('CREATE_VERSION');
    // Infrastructure toggle for the new version / clone produced by this status change.
    const [useNewInfrastructure, setUseNewInfrastructure] = useState(DEFAULT_USE_NEW_INFRASTRUCTURE);
    // Multi-step state
    const [currentStep, setCurrentStep] = useState('status');
    // Clone form fields
    const [cloneTitle, setCloneTitle] = useState('');
    const [cloneDescription, setCloneDescription] = useState('');
    // Clone title validation
    const [cloneTitleError, setCloneTitleError] = useState('');
    const [isValidatingTitle, setIsValidatingTitle] = useState(false);
    // Debounced title validation
    const validateCloneTitle = useDebouncedCallback(async (titleValue) => {
        if (!titleValue.trim()) {
            setCloneTitleError(translate('Title is required'));
            setIsValidatingTitle(false);
            return;
        }
        setIsValidatingTitle(true);
        try {
            const errorMessage = await checkViewNameUnique(titleValue);
            if (errorMessage) {
                setCloneTitleError(errorMessage);
            }
            else {
                setCloneTitleError('');
            }
        }
        catch (error) {
            // If validation fails, allow user to proceed
            setCloneTitleError('');
        }
        finally {
            setIsValidatingTitle(false);
        }
    }, 500);
    // Trigger validation when clone title changes
    useEffect(() => {
        if (currentStep === 'clone-form' && cloneTitle) {
            validateCloneTitle(cloneTitle);
        }
        else if (currentStep === 'clone-form') {
            setCloneTitleError('');
        }
    }, [cloneTitle, currentStep, validateCloneTitle]);
    // Check if we're transitioning from Ready for Use to In Progress
    const isReadyToInProgress = dataView.status === 'PUBLISHED' && newStatus?.value === 'DRAFT';
    // Initialize clone title when entering clone form
    useEffect(() => {
        if (currentStep === 'clone-form' && !cloneTitle) {
            setCloneTitle(dataView.name);
        }
    }, [currentStep, cloneTitle, dataView.name]);
    const handleSubmit = async () => {
        if (isApiLoading) {
            return;
        }
        try {
            if (isReadyToInProgress) {
                if (selectedAction === 'CREATE_VERSION') {
                    // SSP-4186: create the new version and archive the original atomically.
                    // A create-succeeds/archive-fails sequence would otherwise leave both
                    // versions active and duplicate the dataset in the Library list.
                    const result = await performAtomicVersionUpgrade({
                        previous: dataView,
                        createVersion: () => cloneDataView(dataView, 'CREATE_VERSION', { useNewInfrastructure }, { silent: true }),
                        archive: (resource) => archiveDataView(resource, { silent: true }),
                    });
                    if (result.outcome === 'create_failed') {
                        addNotification({
                            title: translate('Version upgrade failed'),
                            subtitle: translate('Could not create the new dataset version. No changes were made.'),
                            type: 'error',
                        });
                        return;
                    }
                    if (result.outcome === 'archive_failed') {
                        addNotification({
                            title: translate('Version upgrade failed'),
                            subtitle: translate('Could not archive the previous version. No changes were kept.'),
                            type: 'error',
                        });
                        return;
                    }
                    addNotification({
                        title: translate('New dataset version created'),
                        type: 'success',
                    });
                    onStatusChange(result.newResource);
                    onClose();
                }
                else {
                    // Clone asset - show clone form step
                    setCurrentStep('clone-form');
                }
            }
            else {
                // Regular status change
                if (!newStatus) {
                    return;
                }
                const statusToSet = newStatus.value;
                const updates = { status: statusToSet };
                const updatedView = await updateDataView(dataView.id, updates);
                onStatusChange(updatedView);
                onClose();
            }
        }
        catch (error) {
            // Error already handled in hook
        }
    };
    const handleCloneSubmit = async () => {
        if (isApiLoading || !cloneTitle.trim() || cloneTitleError) {
            return;
        }
        // Final uniqueness check before submit
        setIsValidatingTitle(true);
        try {
            const errorMessage = await checkViewNameUnique(cloneTitle.trim());
            if (errorMessage) {
                setCloneTitleError(errorMessage);
                setIsValidatingTitle(false);
                return;
            }
        }
        catch (error) {
            // Allow to proceed if check fails
        }
        finally {
            setIsValidatingTitle(false);
        }
        try {
            // Create clone (new variant) - original stays as Ready for Use
            // Pass name and description overrides directly to avoid a second API call
            const newView = await cloneDataView(dataView, 'CLONE', {
                name: cloneTitle.trim(),
                description: cloneDescription.trim(),
                useNewInfrastructure,
            });
            onStatusChange(newView);
            onClose();
        }
        catch (error) {
            // Error already handled in hook
        }
    };
    const handleBack = () => {
        setCurrentStep('status');
    };
    // Determine button label
    const getSubmitButtonLabel = () => {
        if (currentStep === 'clone-form') {
            return translate('Clone');
        }
        if (isReadyToInProgress && selectedAction === 'CLONE') {
            return translate('Clone asset');
        }
        return translate('Change status');
    };
    const isSubmitDisabled = () => {
        if (currentStep === 'clone-form') {
            return isApiLoading || !cloneTitle.trim() || !!cloneTitleError || isValidatingTitle;
        }
        return isApiLoading || !newStatus;
    };
    const submitButton = {
        label: getSubmitButtonLabel(),
        onClick: currentStep === 'clone-form' ? handleCloneSubmit : handleSubmit,
        disabled: isSubmitDisabled(),
    };
    const footerSecondaryActionButtons = currentStep === 'clone-form'
        ? [
            {
                label: translate('Cancel'),
                onClick: onClose,
                disabled: isApiLoading,
            },
            {
                label: translate('Back'),
                onClick: handleBack,
                disabled: isApiLoading,
            },
        ]
        : [
            {
                label: translate('Cancel'),
                onClick: onClose,
                disabled: isApiLoading,
            },
        ];
    const getModalSubtitle = () => {
        if (currentStep === 'clone-form') {
            return translate('Enter information for cloning.');
        }
        return undefined;
    };
    return (_jsxs(ModalWindow, { title: translate('Change status'), subtitle: getModalSubtitle(), isOpen: true, closeWindow: onClose, footerPrimaryActionButton: submitButton, footerSecondaryActionButtons: footerSecondaryActionButtons, children: [currentStep === 'status' && (_jsxs("div", { className: 'space-y-6 p-4', children: [_jsxs("div", { className: 'space-y-1', children: [_jsx(Text, { className: 'text-text-secondary', children: translate('In progress asset can be edited.') }), _jsx(Text, { className: 'text-text-secondary', children: translate('Ready asset can be viewed by users without edit access.') })] }), _jsxs("div", { className: 'flex gap-12 items-center', children: [_jsx(Text, { children: translate('Current status') }), _jsx(Badge, { variant: getStatusBadgeVariant(dataView.status), label: getStatusLabel(dataView.status, translate) })] }), availableOptions.length === 1 ? (_jsxs("div", { className: 'flex gap-12 items-center', children: [_jsx(Text, { children: translate('New status') }), _jsx(Badge, { variant: getStatusBadgeVariant(newStatus?.value), label: getStatusLabel(newStatus?.value, translate) })] })) : (_jsx(Select, { label: translate('New status'), options: availableOptions, value: newStatus, onChange: (value) => setNewStatus(value), width: 'full' })), isReadyToInProgress && (_jsxs("div", { className: 'space-y-4', children: [_jsx(Text, { className: 'font-medium', children: translate('Action') }), _jsx(RadioGroup, { value: selectedAction, onChange: setSelectedAction, children: _jsxs("div", { className: 'space-y-4', children: [_jsx("div", { className: `p-4 border border-solid rounded-lg cursor-pointer ${selectedAction === 'CREATE_VERSION'
                                                ? 'bg-informative-50 border-informative-600 border-[1.5px]'
                                                : 'bg-surface border-neutral-200 hover:bg-neutral-50'}`, onClick: () => setSelectedAction('CREATE_VERSION'), children: _jsx(RadioGroupOption, { option: {
                                                    value: 'CREATE_VERSION',
                                                    label: translate('Create new version'),
                                                    description: translate('Replace the current asset with a new version. Previous versions become archived and made read-only in the library.'),
                                                } }) }), _jsx("div", { className: `p-4 border border-solid rounded-lg cursor-pointer ${selectedAction === 'CLONE'
                                                ? 'bg-informative-50 border-informative-600 border-[1.5px]'
                                                : 'bg-surface border-neutral-200 hover:bg-neutral-50'}`, onClick: () => setSelectedAction('CLONE'), children: _jsx(RadioGroupOption, { option: {
                                                    value: 'CLONE',
                                                    label: translate('Clone asset'),
                                                    description: translate('Cloning an asset allows the existing one to remain available in the library while you create a new one. The status of the existing asset will not change.'),
                                                } }) })] }) }), _jsx(Toggle, { label: translate('Use new infrastructure'), description: translate('Enable this for studies that require only Europe (EU) environment.'), checked: useNewInfrastructure, onChange: setUseNewInfrastructure, dataTestId: 'use-new-infrastructure-toggle' })] }))] })), currentStep === 'clone-form' && (_jsxs("div", { className: 'space-y-6 p-4', children: [_jsxs("div", { className: 'relative', children: [_jsx(Input, { id: 'clone-title', label: translate('Title'), value: cloneTitle, onChange: (e) => {
                                    setCloneTitle(e.target.value);
                                    setCloneTitleError('');
                                }, width: 'full', required: true, hasError: !!cloneTitleError, errorMessage: cloneTitleError, autoComplete: 'off' }), isValidatingTitle && (_jsx("div", { className: 'absolute end-0 top-0', children: _jsx(Spinner, { size: 'sm' }) }))] }), _jsx(TextArea, { id: 'clone-description', label: translate('Description'), value: cloneDescription, onChange: (e) => setCloneDescription(e.target.value), width: 'full' })] }))] }));
};
